import { type CheckboxChangeEvent } from "antd/lib/checkbox/Checkbox";
import { useCallback } from "react";

import { BTCheckbox, BTSection, BTText } from "@btnet/core-components";

import { CommonConstants } from "types/constants";
import { MediaType } from "types/enum";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTFormItem, BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTFormattedPlural } from "commonComponents/btWrappers/BTFormattedPlural/BTFormattedPlural";
import { BTIconCheckCircleOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTSelectUser } from "commonComponents/btWrappers/BTSelectUser/BTSelectUser";
import {
    type IMediaViewingPermissionsFormValues,
    type MediaViewingPermissionsEntity,
    UsersAmount,
} from "commonComponents/entity/media/MediaViewingPermissionsInternal/MediaViewingPermissionsInternal.api.types";
import "commonComponents/entity/media/MediaViewingPermissionsInternal/MediaViewingPermissionsInternal.less";

import { MediaTypeNames } from "entity/media/common/mediaTypes";

import { type IInternalUserExtraData } from "./MediaViewingPermissionsInternal.api.types";

interface IMediaViewingPermissionsProps {
    areDropdownsHidden?: boolean;
    disabled?: boolean;
    documentCount?: number;
    entity: MediaViewingPermissionsEntity;
    isFolder?: boolean;
    mediaType: MediaType;
    sectionHeader?: string;
    values: IMediaViewingPermissionsFormValues;
    handleChange: (e: CheckboxChangeEvent) => void;
    onShowDropdowns?: () => void;
    setFieldTouched: (field: string, isTouched?: boolean) => void;
    setFieldValue: (field: string, value: number[]) => void;
}

const defaultOnShowDropdowns = () => {};
function MediaViewingPermissionsInternal({
    areDropdownsHidden,
    disabled,
    documentCount,
    entity,
    isFolder,
    mediaType,
    sectionHeader,
    values,
    handleChange,
    onShowDropdowns = defaultOnShowDropdowns,
    setFieldTouched,
    setFieldValue,
}: Readonly<IMediaViewingPermissionsProps>) {
    const handleShowOwnerChange = useCallback(
        (e: CheckboxChangeEvent) => {
            handleChange(e);
        },
        [handleChange],
    );

    const handleShowSubsChange = useCallback(
        (e: CheckboxChangeEvent) => {
            handleChange(e);
        },
        [handleChange],
    );

    const getDropdownPlaceholderText = useCallback(
        (mediaName: string) => {
            let internalUsersAmount;
            if (values.internalUserIds.length === 0) {
                internalUsersAmount = UsersAmount.None;
            } else if (values.internalUserIds.length === entity.internalUsers?.length) {
                internalUsersAmount = UsersAmount.All;
            } else {
                internalUsersAmount = UsersAmount.Some;
            }

            let subsAmount;
            if (!entity.showSubs || values.subIds?.length === 0) {
                subsAmount = UsersAmount.None;
            } else if (values.subIds.length === entity.subs?.length) {
                subsAmount = UsersAmount.All;
            } else {
                subsAmount = UsersAmount.Some;
            }

            const areInternalUsersSubsMatching = internalUsersAmount === subsAmount;

            const allText = " on this job";
            const someText = " who have access to the parent folder";

            let internalUserText;
            if (internalUsersAmount === UsersAmount.None) {
                internalUserText = "";
            } else {
                internalUserText = (
                    <>
                        All <b>{CommonConstants.InternalUser.plural.toLocaleLowerCase()}</b>
                    </>
                );
                if (internalUsersAmount === UsersAmount.All) {
                    internalUserText = (
                        <>
                            {internalUserText}
                            {!areInternalUsersSubsMatching && allText}
                        </>
                    );
                } else {
                    internalUserText = (
                        <>
                            {internalUserText}
                            {!areInternalUsersSubsMatching && someText}
                        </>
                    );
                }
            }

            let subText;
            if (subsAmount === UsersAmount.None) {
                subText = "";
            } else {
                subText = (
                    <>
                        {!areInternalUsersSubsMatching && (
                            <>{internalUsersAmount === UsersAmount.None ? "A" : "a"}ll </>
                        )}
                        <b>{CommonConstants.Sub.plural.toLocaleLowerCase()}</b>
                    </>
                );
                if (subsAmount === UsersAmount.All) {
                    subText = (
                        <>
                            {subText}
                            {allText}
                        </>
                    );
                } else {
                    subText = (
                        <>
                            {subText}
                            {someText}
                        </>
                    );
                }
            }

            const trailingText = (
                <>
                    {" "}
                    have access to{" "}
                    <BTFormattedPlural
                        value={documentCount ?? 0}
                        zero="this"
                        one="this"
                        other="these"
                    />{" "}
                    {mediaName}.
                </>
            );

            let finalText;
            if (internalUsersAmount === UsersAmount.None && subsAmount === UsersAmount.None) {
                finalText = (
                    <>
                        No <b>{CommonConstants.InternalUser.plural.toLocaleLowerCase()}</b> or{" "}
                        <b>{CommonConstants.Sub.plural.toLocaleLowerCase()}</b>
                        {trailingText}
                    </>
                );
            } else if (
                internalUsersAmount === UsersAmount.None ||
                subsAmount === UsersAmount.None
            ) {
                finalText = (
                    <>
                        {internalUserText}
                        {subText}
                        {trailingText}
                    </>
                );
            } else {
                finalText = (
                    <>
                        {internalUserText} and {subText}
                        {trailingText}
                    </>
                );
            }

            return finalText;
        },
        [
            documentCount,
            entity.internalUsers,
            entity.showSubs,
            entity.subs,
            values.internalUserIds,
            values.subIds,
        ],
    );

    const handleInternalUsersChanged = useCallback(
        (field: string, value: number[]) => {
            setFieldValue(field, value);
        },
        [setFieldValue],
    );

    const handleSubsChanged = useCallback(
        (field: string, value: number[]) => {
            setFieldValue(field, value);
        },
        [setFieldValue],
    );

    let mediaName;
    if (mediaType === MediaType.Photo && isFolder) {
        mediaName = "folders";
    } else if (documentCount! > 1) {
        mediaName = MediaTypeNames.get(mediaType)!.plural.toLowerCase();
    } else {
        mediaName = MediaTypeNames.get(mediaType)!.singular.toLowerCase();
    }

    let usersDropdowns = <></>;
    const subsDisabledText = (
        <BTFormItemAutomatic<IMediaViewingPermissionsFormValues>
            id="showSubs"
            label={CommonConstants.Sub.plural}
        >
            Disabled on this folder
        </BTFormItemAutomatic>
    );

    if (!isFolder && mediaType !== MediaType.Photo) {
        if (areDropdownsHidden) {
            usersDropdowns = (
                <BTRow align="middle">
                    <BTCol xs={15}>
                        <BTFormItem
                            label={`${CommonConstants.InternalUser.plural} and ${CommonConstants.Sub.plural}`}
                        >
                            <div className="flex">
                                <BTIconCheckCircleOutlined
                                    size="large"
                                    className="margin-right-xs MediaViewingPermissions-Success"
                                />
                                <div>{getDropdownPlaceholderText(mediaName)}</div>
                            </div>
                        </BTFormItem>
                    </BTCol>
                    <BTCol xs={9} className="flex justify-content-end">
                        <BTButtonDeprecated
                            data-testid="editUsersSubs"
                            type="link"
                            disabled={disabled}
                            onClick={onShowDropdowns}
                        >
                            Edit
                        </BTButtonDeprecated>
                    </BTCol>
                </BTRow>
            );
        } else {
            usersDropdowns = (
                <>
                    <BTFormItemAutomatic<IMediaViewingPermissionsFormValues>
                        id="internalUserIds"
                        label={CommonConstants.InternalUser.plural}
                    >
                        <BTSelectUser<IMediaViewingPermissionsFormValues, IInternalUserExtraData>
                            id="internalUserIds"
                            data-testid="internalUserIds"
                            value={values.internalUserIds}
                            multiple={true}
                            disabled={disabled}
                            onChange={handleInternalUsersChanged}
                            onBlur={setFieldTouched}
                            treeData={entity.internalUsers}
                        />
                    </BTFormItemAutomatic>

                    {entity.showSubs ? (
                        <BTFormItemAutomatic<IMediaViewingPermissionsFormValues>
                            id="subIds"
                            label={CommonConstants.Sub.plural}
                        >
                            <BTSelectUser<IMediaViewingPermissionsFormValues>
                                id="subIds"
                                data-testid="subIds"
                                value={values.subIds}
                                multiple={true}
                                disabled={disabled}
                                onChange={handleSubsChanged}
                                onBlur={setFieldTouched}
                                treeData={entity.subs}
                            />
                        </BTFormItemAutomatic>
                    ) : (
                        subsDisabledText
                    )}
                </>
            );
        }
    }

    let showSubs = <></>;
    if (mediaType === MediaType.Photo || isFolder) {
        if (entity.showSubs) {
            showSubs = (
                <BTFormItemAutomatic<IMediaViewingPermissionsFormValues>
                    id="showSubs"
                    label={CommonConstants.Sub.plural}
                >
                    <BTCheckbox
                        id="showSubs"
                        data-testid="showSubs"
                        disabled={disabled}
                        checked={values.showSubs}
                        onChange={handleShowSubsChange}
                    >
                        Can view {mediaName}
                    </BTCheckbox>
                </BTFormItemAutomatic>
            );
        } else {
            showSubs = subsDisabledText;
        }
    }

    const showOwner = entity.showOwner ? (
        <BTFormItemAutomatic<IMediaViewingPermissionsFormValues>
            id="showOwner"
            label={CommonConstants.Client.plural}
        >
            <BTCheckbox
                id="showOwner"
                data-testid="showOwner"
                disabled={disabled}
                checked={values.showOwner}
                onChange={handleShowOwnerChange}
            >
                Can view {mediaName}
            </BTCheckbox>
        </BTFormItemAutomatic>
    ) : (
        <BTFormItemAutomatic<IMediaViewingPermissionsFormValues>
            id="showOwner"
            label={CommonConstants.Client.plural}
        >
            Disabled on this folder
        </BTFormItemAutomatic>
    );

    return (
        <BTSection>
            <BTSection.Header>
                <BTText size="distinct-md" tag="h2">
                    {sectionHeader}
                </BTText>
            </BTSection.Header>
            {usersDropdowns}
            {showSubs}
            {showOwner}
        </BTSection>
    );
}

export default MediaViewingPermissionsInternal;
